import React from 'react';

const ITPDiagram = ({
                        width                 = '100%',
                        height                = '100%',
                        viewBox               = '0 0 350 350',
                        preserveAspectRatio   = 'xMidYMid meet',

                        // petal fills
                        fillHumble            = '#4CAF50',
                        fillHungry            = '#FFC107',
                        fillSmart             = '#2196F3',

                        // strokes & text…
                        strokeColor           = '#ffffff',
                        strokeWidth           = 2,
                        fontFamily            = 'Montserrat, sans-serif',
                        labelFontSize         = 18,
                        textHumble            = '#fff',
                        textHungry            = '#fff',
                        textSmart             = '#fff',
                        textMain              = '#1C2030',
                        titleTextSize         = '14pt',
                        subtitleTextSize      = '12pt',
                        radius = 120,
                        mainTextOffset = 5,
                        smartYPaddingOffset = 30,
                        hungryYPaddingOffset = 45,
                        humbleYPaddingOffset = 45,
                        smartRotPaddingOffset = 0,
                        hungryRotPaddingOffset = 58,
                        humbleRotPaddingOffset = 58,
                        overlapFill = '#fff',
                    }) => {
    const r = radius;
    const C = {
        humble: { x: 135, y: 150 },
        hungry: { x: 214, y: 150 },
        smart:  { x: 175, y: 215 },
    };

    // label data
    const labels = {
        humble: { dx: 0, dy: -r + humbleYPaddingOffset, rot: -humbleRotPaddingOffset, text: 'HUMBLE', fill: textHumble },
        hungry: { dx: 0, dy: -r + hungryYPaddingOffset, rot:  hungryRotPaddingOffset, text: 'HUNGRY', fill: textHungry },
        smart:  { dx: 0, dy:  r - smartYPaddingOffset, rot:  smartRotPaddingOffset, text: 'SMART',  fill: textSmart  },
    };

    const centerX = (C.humble.x + C.hungry.x + C.smart.x) / 3;
    const centerY = (C.humble.y + C.hungry.y + C.smart.y) / 3;
    const strokeWidthValue = parseFloat(String(strokeWidth)) - 0.5;

    // The overlap ("lens") between two equal-radius circles, as an explicit path.
    // Masks and <use> are flattened into transparency groups with a soft mask when
    // the page is printed to PDF, and some PDF viewers (Safari) paint those wrong —
    // the mask backdrop leaks through as hard-edged rectangles over the diagram and
    // its centre text. Plain paths survive the round-trip, so the petals are drawn
    // as solid circles with the overlaps painted back out on top.
    const lensPath = (c1, c2) => {
        const dx = c2.x - c1.x;
        const dy = c2.y - c1.y;
        const d = Math.hypot(dx, dy);
        if (!d || d >= 2 * r) return null;

        const a = d / 2;                       // distance from c1 to the chord
        const h = Math.sqrt(r * r - a * a);    // half the chord length
        const ux = dx / d;
        const uy = dy / d;
        const mx = c1.x + ux * a;              // chord midpoint
        const my = c1.y + uy * a;

        const p1x = (mx - uy * h).toFixed(3);
        const p1y = (my + ux * h).toFixed(3);
        const p2x = (mx + uy * h).toFixed(3);
        const p2y = (my - ux * h).toFixed(3);

        return `M ${p1x} ${p1y} A ${r} ${r} 0 0 0 ${p2x} ${p2y} A ${r} ${r} 0 0 0 ${p1x} ${p1y} Z`;
    };

    const overlaps = [
        { key: 'humble-hungry', d: lensPath(C.humble, C.hungry) },
        { key: 'humble-smart',  d: lensPath(C.humble, C.smart)  },
        { key: 'hungry-smart',  d: lensPath(C.hungry, C.smart)  },
    ].filter(({ d }) => d);

    return (
        <svg
            width={width}
            height={height}
            viewBox={viewBox}
            preserveAspectRatio={preserveAspectRatio}
            xmlns="http://www.w3.org/2000/svg"
        >
            {/** 1) fill the three circles **/}
            <circle cx={C.humble.x} cy={C.humble.y} r={r} fill={fillHumble} />
            <circle cx={C.hungry.x} cy={C.hungry.y} r={r} fill={fillHungry} />
            <circle cx={C.smart.x } cy={C.smart.y } r={r} fill={fillSmart}  />

            {/** 2) knock the overlaps back out so only the petals stay coloured **/}
            {overlaps.map(({ key, d }) => (
                <path key={key} d={d} fill={overlapFill} />
            ))}

            {/** 3) draw the circle outlines **/}
            <circle cx={C.humble.x} cy={C.humble.y} r={r} fill="none" stroke={strokeColor} strokeWidth={strokeWidthValue} />
            <circle cx={C.hungry.x} cy={C.hungry.y} r={r} fill="none" stroke={strokeColor} strokeWidth={strokeWidthValue} />
            <circle cx={C.smart.x } cy={C.smart.y } r={r} fill="none" stroke={strokeColor} strokeWidth={strokeWidthValue} />

            {/** 5) labels **/}
            {Object.entries(labels).map(([key, { dx, dy, rot, text, fill }]) => {
                const { x, y } = C[key];
                return (
                    <text
                        key={key}
                        x={x + dx} y={y + dy}
                        transform={`rotate(${rot} ${x} ${y})`}
                        textAnchor="middle"
                        fontFamily={fontFamily}
                        fontSize={labelFontSize}
                        fontWeight="bold"
                        fill={fill}
                    >
                        {text}
                    </text>
                );
            })}

            {/** 6) center title **/}
            <text textAnchor="middle" fill={textMain} fontFamily={fontFamily}>
                <tspan
                    x={centerX}
                    y={centerY + mainTextOffset}
                    fontSize={titleTextSize}
                    className={'diagram-title'}
                >
                    T<tspan className={'diagram-subtitle'} fontSize={subtitleTextSize}>HE</tspan>&nbsp;I<tspan className={'diagram-subtitle'} fontSize={subtitleTextSize}>DEAL</tspan>
                </tspan>
                <tspan
                    x={centerX}
                    dy="0.8em"
                    fontSize={titleTextSize}
                    className={'diagram-title'}
                >
                    T<tspan className={'diagram-subtitle'} fontSize={subtitleTextSize}>EAM</tspan>&nbsp;P<tspan className={'diagram-subtitle'} fontSize={subtitleTextSize}>LAYER</tspan>
                </tspan>
            </text>
        </svg>
    );
};

export default ITPDiagram;
